import React from 'react';
import { CheckCircle2, Mic, Star, Video } from 'lucide-react';

// A mock live lesson that plays on a 16-second CSS loop: the speaker's tile
// lights up, the transcript fills in, a correction pops, progress grows.
// Pure CSS (see styles/landing-motion.css); no data, nothing to load.
const Tile = ({ who, initials, color, ring, role }) => (
  <div className="lesson-tile rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 aspect-[4/3] flex flex-col items-center justify-center text-white">
    <span className={`speak-ring ${ring}`} aria-hidden="true" />
    <span className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full ${color} flex items-center justify-center text-lg font-bold shadow-lg`}>{initials}</span>
    <span className="mt-2 text-sm font-semibold">{who}</span>
    <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
      {role}
      <span className="talk-wave text-gold-300" aria-hidden="true"><i /><i /><i /></span>
    </span>
  </div>
);

const Bubble = ({ cls, teacher, children }) => (
  <div className={`msg ${cls} flex ${teacher ? 'justify-start' : 'justify-end'}`}>
    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${teacher
      ? 'rounded-bl-sm bg-white dark:bg-slate-800 text-navy dark:text-white border border-primary/10 dark:border-white/10'
      : 'rounded-br-sm bg-primary text-white'}`}>
      {children}
    </div>
  </div>
);

const LessonDemo = () => (
  <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" data-testid="lesson-demo" data-motion-zone>
    <div className="reveal max-w-[1440px] mx-auto grid lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
      <div>
        <p className="text-sm font-semibold text-gold-600 dark:text-gold-300 mb-2">See a lesson in action</p>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy dark:text-white mb-2 leading-tight">Talk, get gentle corrections, and keep improving</h2>
        <span className="draw-line block h-1 w-20 rounded-full bg-gradient-to-r from-primary to-gold-400 mb-4" aria-hidden="true" />
        <ul className="space-y-3 text-text-secondary">
          {['Live 1-on-1 video, right in your browser', 'Your teacher corrects you as you speak', 'Notes after class so you can review what you learned'].map((t) => (
            <li key={t} className="flex gap-2"><CheckCircle2 className="w-5 h-5 text-primary dark:text-gold-300 shrink-0 mt-0.5" />{t}</li>
          ))}
        </ul>
      </div>

      <div data-tilt className="rounded-3xl bg-white/80 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 shadow-card-hover p-4 sm:p-5" aria-label="Example of a lesson" role="img">
        <div className="flex items-center justify-between mb-3 text-xs">
          <span className="flex items-center gap-1.5 font-semibold text-text-primary">
            <span className="rec-dot w-2 h-2 rounded-full bg-red-500" /> Live lesson · Conversation
          </span>
          <span className="flex items-center gap-2 text-text-muted"><Video className="w-4 h-4" /><Mic className="w-4 h-4" /></span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Tile who="Maria" initials="MJ" color="bg-gold-500" ring="speak-teacher" role="Teacher" />
          <Tile who="You" initials="YO" color="bg-primary" ring="speak-student" role="Student" />
        </div>
        <div className="mt-4 space-y-2 min-h-[13.5rem]">
          <Bubble cls="msg-1" teacher>How was your weekend? 😊</Bubble>
          <Bubble cls="msg-2">I go to the beach with my family.</Bubble>
          <Bubble cls="msg-3" teacher>
            <span className="fix-chip items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-0.5 mb-1">
              go → went <CheckCircle2 className="w-3.5 h-3.5" />
            </span>
            <span className="block">Nice! We say: <strong>I went</strong> to the beach.</span>
          </Bubble>
          <Bubble cls="msg-4">I went to the beach! 🏖️</Bubble>
          <Bubble cls="msg-5" teacher>
            <span className="inline-flex items-center gap-1.5">Perfect! <Star className="star-burst w-4 h-4 fill-gold-400 text-gold-400" /> +1 new phrase</span>
          </Bubble>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-text-muted mb-1"><span>What you practised</span><span>Notes shared after class</span></div>
          <div className="h-2 rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
            <div className="learn-bar h-full w-full rounded-full bg-gradient-to-r from-primary to-gold-400" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default LessonDemo;
